import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TreeNode } from 'primeng/api';
import { TreeModule } from 'primeng/tree';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';

import { MeasurementsService } from '@cosmos/angular/api';
import { ClipboardTableService, EntitlementService, ErrorService, PanelService } from '@cosmos/angular/utils';
import { StageTreeMeasurementNode, StageTreeNode, SummaryResponse } from '@cosmos/shared';

import { DataTableComponent } from '../../data/data-table/data-table.component';
import { ColumnDefinition } from '../../data/data-table/column-definition.model';
import { SUMMARY_TYPE_REGISTRY } from './registry/summary-type-registry';

type WizardStep = 'select' | 'configure' | 'results';

interface ResultSection {
  type: string;
  supported: boolean;
  columns?: ColumnDefinition[];
  data?: Record<string, unknown>[];
  count?: number;
}

/** One per-type block on the configure step. */
interface ConfigSection {
  type: string;
  label: string;
  form: UntypedFormGroup;
  fields: FormlyFieldConfig[];
  model: Record<string, unknown>;
}

@Component({
  selector: 'cosmos-summarize-measurements',
  templateUrl: './summarize-measurements.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule, FormlyModule, TranslatePipe,
    TreeModule, TagModule, ButtonModule, CardModule, DividerModule, MessageModule,
    DataTableComponent,
  ],
})
export class SummarizeMeasurementsComponent {
  /** A single subtree (invoked from a stage) or a forest (future: project root). */
  readonly stageTree = input.required<StageTreeNode | StageTreeNode[]>();

  private readonly translate = inject(TranslateService);
  private readonly measurementsService = inject(MeasurementsService);
  private readonly errorService = inject(ErrorService);
  private readonly entitlement = inject(EntitlementService);
  private readonly clipboardTable = inject(ClipboardTableService);
  private readonly datePipe = inject(DatePipe);
  protected readonly panelService = inject(PanelService);

  // ── wizard state ─────────────────────────────────────────────
  readonly step = signal<WizardStep>('select');
  readonly loading = signal(false);
  readonly exporting = signal(false);

  // Step 1 — tree is derived from the required input.
  readonly treeNodes = computed<TreeNode[]>(() => this.buildTree(this.asForest()));
  selectedNodes: TreeNode[] = [];

  // Step 2 — one form + model per supported measurement type, kept as plain
  // objects (Formly mutates them in place) with a manual `configTick` bump so
  // `canSummarize` recomputes on every edit.
  readonly groupedIds = signal<Record<string, string[]>>({});
  configSections: ConfigSection[] = [];
  private readonly configTick = signal(0);

  // Step 3
  readonly response = signal<SummaryResponse | null>(null);

  private readonly advancedOptions = computed(() =>
    this.entitlement.hasFeature('monitoring', 'monitoring.summarize.advanced_options' as any),
  );

  readonly supportedTypes = computed(() =>
    Object.keys(this.groupedIds()).filter(t => !!SUMMARY_TYPE_REGISTRY[t]),
  );
  readonly unsupportedTypes = computed(() =>
    Object.keys(this.groupedIds()).filter(t => !SUMMARY_TYPE_REGISTRY[t]),
  );

  readonly canSummarize = computed(() => {
    this.configTick();
    const sections = this.configSections;
    if (!sections.length) return false;
    return sections.every(s => SUMMARY_TYPE_REGISTRY[s.type].validate(s.model));
  });

  onConfigChange(section: ConfigSection, model: Record<string, unknown>): void {
    // ngx-formly v7 emits a fresh object on (modelChange); keep our reference in sync.
    section.model = model;
    this.configTick.update(n => n + 1);
  }

  readonly resultSections = computed<ResultSection[]>(() => {
    const res = this.response();
    if (!res) return [];
    return Object.entries(res).map(([type, result]): ResultSection => {
      if (!result.supported) return { type, supported: false };
      const ui = SUMMARY_TYPE_REGISTRY[type];
      if (!ui) return { type, supported: false };
      const { columns, data } = ui.buildTable(
        result.measurements,
        result.config as Record<string, unknown>,
        this.translate,
      );
      return { type, supported: true, columns, data, count: result.measurements.length };
    });
  });

  private asForest(): StageTreeNode[] {
    const t = this.stageTree();
    return Array.isArray(t) ? t : [t];
  }

  /** Stage nodes are containers; measurement leaves carry the id + type. */
  buildTree(stages: StageTreeNode[]): TreeNode[] {
    return (stages ?? []).map(stage => ({
      label: stage.name,
      data: { type: 'stage', id: stage._id },
      icon: 'fa-solid fa-folder',
      children: [
        ...this.buildTree(stage.children),
        ...(stage.measurements ?? []).map((m: StageTreeMeasurementNode) => ({
          label: m.name,
          data: { type: 'measurement', id: m._id, measurementType: m.measurementType },
          icon: 'fa-solid fa-chart-line',
          leaf: true,
        })),
      ],
    }));
  }

  // ── Step 1 → 2 ───────────────────────────────────────────────
  goToConfigure(): void {
    const grouped: Record<string, string[]> = {};
    for (const node of this.selectedNodes) {
      if (node.data?.type !== 'measurement') continue;
      const type = node.data.measurementType as string;
      (grouped[type] ??= []).push(node.data.id as string);
    }
    this.groupedIds.set(grouped);

    const previous = new Map(this.configSections.map(s => [s.type, s.model]));
    const supported = Object.keys(grouped).filter(t => !!SUMMARY_TYPE_REGISTRY[t]);
    this.configSections = supported.map(type => ({
      type,
      label: this.translate.instant('measurement.types.' + type),
      form: new UntypedFormGroup({}),
      // keep whatever the user already typed for this type if they step back and forth
      model: previous.get(type) ?? {},
      fields: SUMMARY_TYPE_REGISTRY[type].configFields({
        translate: this.translate,
        advancedOptions: this.advancedOptions(),
      }),
    }));
    this.configTick.update(n => n + 1);
    this.step.set('configure');
  }

  backToSelect(): void {
    this.step.set('select');
  }

  // ── Step 2 → 3 ───────────────────────────────────────────────
  runSummary(): void {
    const { ids, config } = this.buildRequest();

    this.loading.set(true);
    this.step.set('results');
    this.measurementsService.getSummary(ids, config).pipe(
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: (res) => this.response.set(res.body as SummaryResponse),
      error: (e) => { this.errorService.handleError(e); this.step.set('configure'); },
    });
  }

  backToConfigure(): void {
    this.response.set(null);
    this.step.set('configure');
  }

  private buildRequest(): { ids: string[]; config: Record<string, unknown> } {
    const grouped = this.groupedIds();
    const ids = this.configSections.flatMap(s => grouped[s.type] ?? []);
    const config: Record<string, unknown> = {};
    for (const s of this.configSections) config[s.type] = s.model;
    return { ids, config };
  }

  // ── Results actions ──────────────────────────────────────────
  async copySection(section: ResultSection): Promise<void> {
    if (!section.supported || !section.columns) return;
    const headers = section.columns.map(c => c.label);
    const rows = (section.data ?? []).map(row =>
      section.columns!.map(c => this.cell(row, c)),
    );
    try {
      await this.clipboardTable.copyTable(headers, rows);
      this.errorService.notify('success', this.translate.instant('summarize-measurements.copy-success'));
    } catch {
      this.errorService.notify('error', this.translate.instant('summarize-measurements.copy-error'));
    }
  }

  /** Mirrors DataTableComponent cell rendering for the column types the registry emits. */
  private cell(row: Record<string, unknown>, col: ColumnDefinition): string {
    const raw = col.name.split('.').reduce<any>((acc, k) => acc?.[k.split('?')[0]], row);
    if (raw == null) return '-';
    if (col.type === 'date') return this.datePipe.transform(raw, 'dd-MM-yyyy HH:mm:ss') ?? '-';
    if (col.type === 'number') return typeof raw === 'number' ? String(Math.round(raw * 100) / 100) : String(raw);
    if (col.type === 'boolean') return this.translate.instant(raw ? 'boolean.yes' : 'boolean.no');
    return String(raw);
  }

  exportXlsx(): void {
    const { ids, config } = this.buildRequest();

    this.exporting.set(true);
    this.measurementsService.downloadSummaryXlsx(ids, { config }).pipe(
      finalize(() => this.exporting.set(false)),
    ).subscribe({
      next: (res) => this.triggerDownload(res.body as Blob, res.headers.get('Content-Disposition')),
      error: (e) => this.errorService.handleError(e),
    });
  }

  private triggerDownload(blob: Blob, contentDisposition: string | null): void {
    const match = contentDisposition?.match(/filename=([^;]+)/);
    const filename = match ? match[1].trim() : `measurement-summary-${Date.now()}.xlsx`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  typeLabel(type: string): string {
    return this.translate.instant('measurement.types.' + type);
  }
}
